import type { LocationPathEntry } from "~/helpers/mobile/location/siteLocationAdapter";
import type { MaterialPrewarmDataset } from "~/helpers/offline/materialPrewarmDatasets";

import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
	MaterialItemCountType,
	MaterialTransactionType,
	ShipMovementOrderInput,
	SiteLocationType
} from "~/gql/graphqlBase.generated";
import { pruneCachedQuantityLocations } from "~/helpers/offline/quantitySnapshot";
import tenantChanged from "~/store/tenantChanged";

// ── Types ──────────────────────────────────────────────────────────────────

/** Lightweight location reference stored in Redux (persisted). */
export interface SelectedLocationInfo {
	locationId: string;
	/** The drill-down path from site to the selected location (for "go up one level"). */
	locationPath: LocationPathEntry[];
	locationType: SiteLocationType | "site";
	name: string;
	siteLocationId: string;
	siteName: string;
}

/** Lightweight cached material item for offline use. */
export interface CachedMaterialItem {
	batchTracked?: boolean;
	category: string;
	description: string;
	erpId: string;
	id: string;
	name: string;
}

/** Cached quantity at a location for offline use. */
export interface CachedLocationQuantity {
	locationId: string;
	materialItemId: string;
	quantity: number;
}

export const CACHED_HISTORY_SCHEMA_VERSION = 2;

/** Cached inventory transaction for offline history display. */
export interface CachedTransaction {
	countType: MaterialItemCountType;
	id: string;
	itemName: string;
	jobNumber?: number | null;
	locationId: string;
	locationName: string;
	lotNumber?: number | null;
	materialItemId: string;
	previousQuantity: number;
	purchaseOrderNumber: string;
	quantity: number;
	reasonCode: string;
	timestamp: string;
	toLocationId: string;
	toLocationName: string;
	transactionType: MaterialTransactionType;
	userEmail: string;
	vendorName: string;
	vendorPurchaseOrderId: string;
}

export type HistoryFilter = "all" | "counts" | "moves" | "receives" | "transfers";

/** A cross-site transfer's ship half, held durably until its create has actually succeeded. */
export interface PendingShip {
	/** The frozen actionKey the create was fired with — matched against SW replay messages. */
	createActionKey: string;
	itemName?: string;
	locationName?: string;
	movementOrderId: string;
	queuedAt: string;
	/** The frozen actionKey applied to shipInput when the deferred ship finally fires. */
	shipActionKey: string;
	shipInput: Omit<ShipMovementOrderInput, "idempotentKey">;
}

export type AssignedCountInFlightPhase = "starting" | "started" | "submitting" | "posted";

/** In-session-only busy marker for an assigned-count task, keyed by the fired action's frozen idempotentKey. */
export interface AssignedCountInFlightEntry {
	actionKey: string;
	phase: AssignedCountInFlightPhase;
	taskKey: string;
}

export type CountConfirmationStatus = "confirmed" | "pending";

/** The countMaterialItem input fields that pass through unchanged onto the resulting MaterialTransactionDocument — wsapi stamps its own receipt-time `timestamp`, so this is the only client-known identity for the row. */
export interface CountConfirmationMatchKey {
	countType: MaterialItemCountType;
	locationId: string;
	materialItemId: string;
	quantity: number;
}

/** A locally-known count/move/receive, shown immediately in History rather than waiting on the server's projection. Retires once a matching server row appears (see history's activityLogHistoryHelpers). */
export interface ActivityLogEntry {
	actionKey: string;
	itemId: string;
	itemName: string;
	locationName: string;
	purchaseOrderNumber?: string;
	quantity: number;
	timestamp: string;
	toLocationName?: string;
	transactionType: MaterialTransactionType;
	userEmail: string;
	vendorName?: string;
}

/** Tracks a countMaterialItem write from submit through SW confirmation, keyed by the idempotencyKey it fired with, so History can wait for that specific row instead of guessing from row counts. */
export interface CountConfirmationWatch {
	actionKey: string;
	matchKey: CountConfirmationMatchKey;
	status: CountConfirmationStatus;
}

export interface MaterialMobileState {
	/** Local activity log for count/move/receive, visible in History before the server row projects (max 500 entries, 7-day cleanup). See ActivityLogEntry. */
	activityLog?: ActivityLogEntry[];
	/** In-session busy overlay for assigned-count tasks, keyed by actionKey. Never persisted. */
	assignedCountInFlight: Record<string, AssignedCountInFlightEntry>;
	/** Cached inventory transactions for offline history display. */
	cachedHistory?: CachedTransaction[];
	/** Schema version the persisted `cachedHistory` rows were written with. Absent = pre-versioned. */
	cachedHistorySchemaVersion?: number;
	/** Cached location quantities for offline use, keyed by locationId. */
	cachedLocationQuantitiesByLocation: Record<string, CachedLocationQuantity[]>;
	/** When each location's cached quantities were last written, keyed by locationId. ISO strings. */
	cachedLocationQuantitiesLastUpdate: Record<string, string>;
	/** Cached material items for offline use. */
	cachedMaterialItems: CachedMaterialItem[];
	/** The single in-flight countMaterialItem write History hasn't confirmed visible yet. Never persisted. */
	countConfirmationWatch?: CountConfirmationWatch;
	/** Active filter for the History tab transaction list. Persists across drilldown navigation. */
	historyFilter: HistoryFilter;
	/** Selected reason codes (as strings) to additionally filter the History tab by. Empty = no filter. */
	historyReasonFilter: string[];
	/** Last time history was refreshed from server. */
	lastHistoryRefresh?: string;
	/** Path from the last selected location, preserved for "go up one level" on Change. */
	lastLocationPath: LocationPathEntry[];
	/** Operation name of the most recent MATERIAL replay success, so quantity consumers can tell what landed. Never persisted. */
	lastQueueSuccessMutation?: string;
	lastSyncTimestamp: string | null;
	/** Activity-log entries staged at submit, keyed by the write's actionKey, held until that write confirms. Durable — a queued count must survive the persist/rehydrate that wipes countConfirmationWatch. */
	pendingActivityLog?: Record<string, ActivityLogEntry>;
	/** Cross-site transfers whose ship half is waiting on the create's SW success. Durable — survives kill/relaunch. */
	pendingShips: Record<string, PendingShip>;
	prewarmedAtByDataset?: Partial<Record<MaterialPrewarmDataset, string>>;
	/** Bumped whenever a MATERIAL mutation replays successfully via the SW so cached quantities know to refetch. */
	queueSuccessSignal: number;
	/** The currently selected working location (site, warehouse, bin, etc.). */
	selectedLocation: SelectedLocationInfo | null;
}

// ── Initial state ──────────────────────────────────────────────────────────

const initialState: MaterialMobileState = {
	activityLog: [],
	assignedCountInFlight: {},
	cachedHistory: [],
	cachedHistorySchemaVersion: CACHED_HISTORY_SCHEMA_VERSION,
	cachedLocationQuantitiesByLocation: {},
	cachedLocationQuantitiesLastUpdate: {},
	cachedMaterialItems: [],
	countConfirmationWatch: undefined,
	historyFilter: "all",
	historyReasonFilter: [],
	lastLocationPath: [],
	lastSyncTimestamp: null,
	pendingActivityLog: {},
	pendingShips: {},
	prewarmedAtByDataset: {},
	queueSuccessSignal: 0,
	selectedLocation: null
};

// ── Slice ──────────────────────────────────────────────────────────────────

export const materialMobileSlice = createSlice({
	extraReducers: (builder) => {
		builder.addCase(tenantChanged, (state) => {
			state.lastLocationPath = [];
			state.selectedLocation = null;
		});
	},
	initialState,
	name: "materialMobile",
	reducers: {
		addActivityLogEntry: (state, action: PayloadAction<ActivityLogEntry>) => {
			if (!state.activityLog) state.activityLog = [];
			if (state.activityLog.some((entry) => entry.actionKey === action.payload.actionKey)) return;
			state.activityLog.unshift(action.payload);
			if (state.activityLog.length > 500) state.activityLog = state.activityLog.slice(0, 500);
		},
		addAssignedCountInFlight: (state, action: PayloadAction<AssignedCountInFlightEntry>) => {
			state.assignedCountInFlight[action.payload.actionKey] = action.payload;
		},
		addPendingShip: (state, action: PayloadAction<PendingShip>) => {
			state.pendingShips[action.payload.movementOrderId] = action.payload;
		},
		bumpQueueSuccessSignal: (state, action: PayloadAction<string | undefined>) => {
			state.queueSuccessSignal += 1;
			state.lastQueueSuccessMutation = action.payload;
		},
		markMaterialDatasetPrewarmed: (state, action: PayloadAction<MaterialPrewarmDataset>) => {
			state.prewarmedAtByDataset = {
				...state.prewarmedAtByDataset,
				[action.payload]: new Date().toISOString()
			};
		},
		cleanupActivityLog: (state) => {
			const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
			state.activityLog = (state.activityLog ?? []).filter((entry) => entry.timestamp > sevenDaysAgo);
			if (!state.pendingActivityLog) {
				state.pendingActivityLog = {};
				return;
			}
			for (const [actionKey, entry] of Object.entries(state.pendingActivityLog)) {
				if (entry.timestamp <= sevenDaysAgo) delete state.pendingActivityLog[actionKey];
			}
		},
		cleanupOldHistory: (state) => {
			if (!state.cachedHistory) {
				state.cachedHistory = [];
				return;
			}
			const fourteenDaysAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString();
			state.cachedHistory = state.cachedHistory.filter((entry) => entry.timestamp > fourteenDaysAgo);
		},
		clearHistoryReasonFilter: (state) => {
			state.historyReasonFilter = [];
		},
		clearAssignedCountInFlight: (state, action: PayloadAction<string>) => {
			delete state.assignedCountInFlight[action.payload];
		},
		clearCountConfirmationWatch: (state, action: PayloadAction<string>) => {
			if (state.countConfirmationWatch?.actionKey === action.payload) state.countConfirmationWatch = undefined;
			if (state.pendingActivityLog) delete state.pendingActivityLog[action.payload];
		},
		confirmCountConfirmationWatch: (state, action: PayloadAction<string>) => {
			if (state.countConfirmationWatch?.actionKey === action.payload) {
				state.countConfirmationWatch.status = "confirmed";
			}
			if (!state.pendingActivityLog) state.pendingActivityLog = {};
			const entry = state.pendingActivityLog[action.payload];
			if (!entry) return;
			delete state.pendingActivityLog[action.payload];
			if (!state.activityLog) state.activityLog = [];
			if (state.activityLog.some((existing) => existing.actionKey === entry.actionKey)) return;
			state.activityLog.unshift(entry);
			if (state.activityLog.length > 500) state.activityLog = state.activityLog.slice(0, 500);
		},
		removePendingShip: (state, action: PayloadAction<string>) => {
			delete state.pendingShips[action.payload];
		},
		removePendingShipByCreateActionKey: (state, action: PayloadAction<string>) => {
			for (const [movementOrderId, pendingShip] of Object.entries(state.pendingShips)) {
				if (pendingShip.createActionKey === action.payload) delete state.pendingShips[movementOrderId];
			}
		},
		setCachedHistory: (state, action: PayloadAction<CachedTransaction[] | null>) => {
			state.cachedHistorySchemaVersion = CACHED_HISTORY_SCHEMA_VERSION;
			if (action.payload === null) {
				state.cachedHistory = undefined;
				return;
			}
			state.cachedHistory = action.payload;
			state.lastHistoryRefresh = new Date().toISOString();
		},
		setCachedLocationQuantities: (
			state,
			action: PayloadAction<{ locationId: string; quantities: CachedLocationQuantity[] }>
		) => {
			state.cachedLocationQuantitiesByLocation[action.payload.locationId] = action.payload.quantities;
			state.cachedLocationQuantitiesLastUpdate[action.payload.locationId] = new Date().toISOString();
			for (const staleLocationId of pruneCachedQuantityLocations({
				lastUpdateByLocation: state.cachedLocationQuantitiesLastUpdate
			})) {
				delete state.cachedLocationQuantitiesByLocation[staleLocationId];
				delete state.cachedLocationQuantitiesLastUpdate[staleLocationId];
			}
		},
		setCachedMaterialItems: (state, action: PayloadAction<CachedMaterialItem[]>) => {
			state.cachedMaterialItems = action.payload;
		},
		setHistoryFilter: (state, action: PayloadAction<HistoryFilter>) => {
			state.historyFilter = action.payload;
		},
		setHistoryReasonFilter: (state, action: PayloadAction<string[]>) => {
			state.historyReasonFilter = action.payload;
		},
		setSelectedLocation: (state, action: PayloadAction<SelectedLocationInfo | null>) => {
			if (action.payload) {
				state.lastLocationPath = action.payload.locationPath;
			}
			state.selectedLocation = action.payload;
		},
		stageActivityLogEntry: (state, action: PayloadAction<ActivityLogEntry>) => {
			if (!state.pendingActivityLog) state.pendingActivityLog = {};
			state.pendingActivityLog[action.payload.actionKey] = action.payload;
		},
		toggleHistoryReason: (state, action: PayloadAction<string>) => {
			if (!Array.isArray(state.historyReasonFilter)) state.historyReasonFilter = [];
			const code = action.payload;
			const idx = state.historyReasonFilter.indexOf(code);
			if (idx >= 0) {
				state.historyReasonFilter.splice(idx, 1);
			} else {
				state.historyReasonFilter.push(code);
			}
		},
		updateLastSync: (state) => {
			state.lastSyncTimestamp = new Date().toISOString();
		},
		watchCountConfirmation: (
			state,
			action: PayloadAction<{ actionKey: string; matchKey: CountConfirmationMatchKey }>
		) => {
			state.countConfirmationWatch = { ...action.payload, status: "pending" };
		}
	}
});

export const {
	addActivityLogEntry,
	addAssignedCountInFlight,
	addPendingShip,
	bumpQueueSuccessSignal,
	cleanupActivityLog,
	cleanupOldHistory,
	clearAssignedCountInFlight,
	clearCountConfirmationWatch,
	clearHistoryReasonFilter,
	confirmCountConfirmationWatch,
	markMaterialDatasetPrewarmed,
	removePendingShip,
	removePendingShipByCreateActionKey,
	setCachedHistory,
	setCachedLocationQuantities,
	setCachedMaterialItems,
	setHistoryFilter,
	setHistoryReasonFilter,
	setSelectedLocation,
	stageActivityLogEntry,
	toggleHistoryReason,
	updateLastSync,
	watchCountConfirmation
} = materialMobileSlice.actions;

export default materialMobileSlice.reducer;
